import { apiCall } from '../utils/api';
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import StyledQRCode from '../components/StyledQRCode';
import { qrStylePresets } from '../utils/qrStyles';

function CreateQR({ user, onLogout }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [name, setName] = useState('');
  const [destinationUrl, setDestinationUrl] = useState('');
  const [shortCode, setShortCode] = useState(Math.random().toString(36).substring(2, 8));
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [errorCorrection, setErrorCorrection] = useState('H');
  const [size, setSize] = useState(800);
  const [logoFile, setLogoFile] = useState(null);
  const [logoFileId, setLogoFileId] = useState(null);
  const [pattern, setPattern] = useState('squares');
  const [logoSize, setLogoSize] = useState(20);
  const [cornerStyle, setCornerStyle] = useState('square');
  const [frameStyle, setFrameStyle] = useState('none');
  const [frameText, setFrameText] = useState('SCAN ME');
  const [frameColor, setFrameColor] = useState('#626ed4');
  const [expirationDate, setExpirationDate] = useState('');
  const [neverExpire, setNeverExpire] = useState(true);

  const qrValue = `${window.location.origin}/r/${shortCode}`;

  const templates = [
    { name: 'Social Media', icon: '📱', config: { fgColor: '#626ed4', bgColor: '#FFFFFF', errorCorrection: 'H', pattern: 'dots', frameStyle: 'rounded', frameColor: '#626ed4' } },
    { name: 'Business Card', icon: '💼', config: { fgColor: '#000000', bgColor: '#FFFFFF', errorCorrection: 'M', pattern: 'squares', frameStyle: 'none', frameColor: '#000000' } },
    { name: 'Product Label', icon: '🏷️', config: { fgColor: '#02a499', bgColor: '#FFFFFF', errorCorrection: 'Q', pattern: 'rounded', frameStyle: 'bottom', frameColor: '#02a499' } },
    { name: 'Event Ticket', icon: '🎫', config: { fgColor: '#6f42c1', bgColor: '#FFFFFF', errorCorrection: 'H', pattern: 'dots', frameStyle: 'circle', frameColor: '#6f42c1' } },
    { name: 'Restaurant Menu', icon: '🍽️', config: { fgColor: '#f1734f', bgColor: '#FFFFFF', errorCorrection: 'M', pattern: 'rounded', frameStyle: 'bottom', frameColor: '#f1734f' } },
    { name: 'Custom', icon: '✨', config: { fgColor: '#000000', bgColor: '#FFFFFF', errorCorrection: 'H', pattern: 'squares', frameStyle: 'none', frameColor: '#626ed4' } }
  ];

  const applyTemplate = (template) => {
    setFgColor(template.config.fgColor);
    setBgColor(template.config.bgColor);
    setErrorCorrection(template.config.errorCorrection);
    setPattern(template.config.pattern);
    setFrameStyle(template.config.frameStyle);
    setFrameColor(template.config.frameColor);
    if (template.config.frameText) {
      setFrameText(template.config.frameText);
    }
    toast.success(`${template.name} style applied`);
  };

  const applyPreset = (preset) => {
    setFgColor(preset.config.fgColor);
    setBgColor(preset.config.bgColor);
    setPattern(preset.config.pattern);
    setCornerStyle(preset.config.cornerStyle);
    setFrameStyle(preset.config.frameStyle);
    setFrameColor(preset.config.frameColor);
    setFrameText(preset.config.frameText);
    setErrorCorrection('H');
    toast.success(`${preset.name} preset applied`);
  };

  const handleNext = () => {
    if (step === 1) {
      if (!name || !destinationUrl) {
        toast.error('Please fill in required fields');
        return;
      }
      try {
        new URL(destinationUrl);
      } catch {
        toast.error('Please enter a valid URL');
        return;
      }
    }
    setStep(step + 1);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await apiCall(`/api/files/logo`, {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        // Read as text first to avoid "body disturbed" error from platform scripts
        const textData = await response.text();
        try {
          const data = JSON.parse(textData);
          setLogoFileId(data.file_id);
          setLogoFile(file);
          toast.success('Logo uploaded');
        } catch (parseError) {
          // If parsing fails, still mark as uploaded
          console.warn('[Logo Upload] Could not parse response, marking as uploaded anyway');
          setLogoFile(file);
          toast.success('Logo uploaded');
        }
      } else {
        toast.error(`Upload failed: Error ${response.status}`);
      }
    } catch (error) {
      console.error('[Logo Upload] Error:', error);
      toast.error(`Failed to upload logo: ${error.message || 'Network error'}`);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    console.log('[QR Creation] Starting save process...');

    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('destination_url', destinationUrl);
      formData.append('short_code', shortCode);
      formData.append('utm_source', utmSource);
      formData.append('utm_medium', utmMedium);
      formData.append('utm_campaign', utmCampaign);
      formData.append('fg_color', fgColor);
      formData.append('bg_color', bgColor);
      formData.append('error_correction', errorCorrection);
      formData.append('size', size);
      formData.append('pattern', pattern);
      formData.append('corner_style', cornerStyle);
      formData.append('logo_size', logoSize);
      formData.append('frame_style', frameStyle);
      formData.append('frame_text', frameText);
      formData.append('frame_color', frameColor);
      if (!neverExpire && expirationDate) {
        formData.append('expiration_date', expirationDate);
      }
      if (logoFileId) {
        formData.append('logo_file_id', logoFileId);
      }

      console.log('[QR Creation] Making API call to:', `/api/projects/${projectId}/qrcodes`);
      
      const response = await apiCall(`/api/projects/${projectId}/qrcodes`, {
        method: 'POST',
        body: formData,
      });

      console.log('[QR Creation] Response status:', response.status);

      if (response.ok) {
        console.log('[QR Creation] Success! Navigating back...');
        toast.success('QR code created!');
        navigate(`/projects/${projectId}`);
      } else {
        // Don't try to read response body - Emergent scripts already consumed it
        // Just show the status code
        console.error('[QR Creation] Error response status:', response.status);
        
        const errorMessage = response.status === 401 
          ? 'Authentication failed. Please login again.'
          : response.status === 409
          ? 'Short code already exists. Try a different code.'
          : response.status === 400
          ? 'Invalid QR code data. Check all fields.'
          : `Failed to create QR code (Error ${response.status})`;
        
        toast.error(errorMessage);
      }
    } catch (error) {
      // Show network or unexpected errors
      console.error('[QR Creation] Exception caught:', error);
      toast.error(`Network error. Please check your connection and try again.`);
    } finally {
      setLoading(false);
      console.log('[QR Creation] Save process complete');
    }
  };

  return (
    <div className="app-layout">
      <Sidebar onLogout={onLogout} user={user} />
      
      <div className="main-content">
        <Topbar user={user} pageTitle="Create QR Code" />
        
        <div className="page-content">
          {/* Breadcrumb */}
          <nav className="breadcrumb">
            <span className="breadcrumb-item" onClick={() => navigate('/projects')} style={{ cursor: 'pointer' }}>Projects</span>
            <span className="breadcrumb-item" onClick={() => navigate(`/projects/${projectId}`)} style={{ cursor: 'pointer' }}>Project</span>
            <span className="breadcrumb-item active">Create QR Code</span>
          </nav>

          {/* Wizard Steps */}
          <div className="wizard-steps mb-4">
            <div className={`step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
              <div className="step-circle">1</div>
              <div className="step-label">Content</div>
            </div>
            <div className="step-connector"></div>
            <div className={`step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
              <div className="step-circle">2</div>
              <div className="step-label">Design</div>
            </div>
            <div className="step-connector"></div>
            <div className={`step ${step >= 3 ? 'active' : ''}`}>
              <div className="step-circle">3</div>
              <div className="step-label">Review</div>
            </div>
          </div>

          {/* Step Content */}
          <div className="row">
            <div className="col-lg-8">
              <div className="card">
                {/* Step 1: Content */}
                {step === 1 && (
                  <div>
                    <h4 className="mb-4">Content & Tracking</h4>
                    
                    <div className="mb-3">
                      <label className="label">QR Code Name *</label>
                      <input
                        type="text"
                        className="input"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="e.g., Summer Sale QR Code"
                        data-testid="qr-name-input"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="label">Destination URL *</label>
                      <input
                        type="url"
                        className="input"
                        value={destinationUrl}
                        onChange={(e) => setDestinationUrl(e.target.value)}
                        placeholder="https://example.com/landing-page"
                        data-testid="destination-url-input"
                      />
                    </div>

                    <div className="mb-4">
                      <label className="label">Short Code</label>
                      <div className="d-flex gap-2">
                        <input
                          type="text"
                          className="input"
                          value={shortCode}
                          onChange={(e) => setShortCode(e.target.value)}
                          style={{ fontFamily: 'monospace', flex: 1 }}
                          data-testid="short-code-input"
                        />
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={() => setShortCode(Math.random().toString(36).substring(2, 8))}
                        >
                          🔄 Random
                        </button>
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem' }}>
                      <h5 className="mb-3">UTM Tracking (Optional)</h5>
                      
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="label">UTM Source</label>
                          <input
                            type="text"
                            className="input"
                            value={utmSource}
                            onChange={(e) => setUtmSource(e.target.value)}
                            placeholder="e.g., qr_code, instagram"
                            data-testid="utm-source-input"
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label className="label">UTM Medium</label>
                          <input
                            type="text"
                            className="input"
                            value={utmMedium}
                            onChange={(e) => setUtmMedium(e.target.value)}
                            placeholder="e.g., print, social"
                            data-testid="utm-medium-input"
                          />
                        </div>
                        <div className="col-12 mb-3">
                          <label className="label">UTM Campaign</label>
                          <input
                            type="text"
                            className="input"
                            value={utmCampaign}
                            onChange={(e) => setUtmCampaign(e.target.value)}
                            placeholder="e.g., summer_2024"
                            data-testid="utm-campaign-input"
                          />
                        </div>
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginTop: '1rem' }}>
                      <h5 className="mb-3">⏰ Expiration (Optional)</h5>
                      <div className="mb-3">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <input
                            type="checkbox"
                            id="never-expire"
                            checked={neverExpire}
                            onChange={(e) => setNeverExpire(e.target.checked)}
                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                          />
                          <label htmlFor="never-expire" style={{ cursor: 'pointer', margin: 0 }}>
                            Never expire (recommended)
                          </label>
                        </div>
                        {!neverExpire && (
                          <div>
                            <label className="label">Expiration Date</label>
                            <input
                              type="datetime-local"
                              className="input"
                              value={expirationDate}
                              onChange={(e) => setExpirationDate(e.target.value)}
                              data-testid="expiration-date-input"
                            />
                            <p className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                              QR code will stop working after this date
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="d-flex justify-content-end mt-4">
                      <button
                        className="btn btn-primary"
                        onClick={handleNext}
                        data-testid="next-step-button"
                      >
                        Next: Design →
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Design */}
                {step === 2 && (
                  <div>
                    <h4 className="mb-4">Design & Customization</h4>

                    {/* Style Presets Gallery */}
                    <div className="mb-4">
                      <label className="label">🎨 Artistic QR Styles - Choose a Preset</label>
                      <p className="text-muted mb-3" style={{ fontSize: '0.875rem' }}>
                        Select from 12+ professional branded QR code styles
                      </p>
                      <div className="grid grid-cols-4" style={{ gap: '0.75rem' }}>
                        {qrStylePresets.map((preset) => (
                          <button
                            key={preset.id}
                            type="button"
                            onClick={() => applyPreset(preset)}
                            className="btn btn-light"
                            style={{
                              padding: '1rem',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '0.5rem',
                              height: '100%',
                              textAlign: 'center'
                            }}
                            data-testid={`preset-${preset.id}`}
                          >
                            <span style={{ fontSize: '2rem' }}>{preset.icon}</span>
                            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>{preset.name}</span>
                            <span style={{ fontSize: '0.7rem', color: 'var(--gray-600)' }}>{preset.description}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginBottom: '1rem' }}>
                      <h5 className="mb-3">Custom Styling</h5>
                    </div>

                    {/* Pattern & Corner */}
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="label">QR Pattern Style</label>
                        <select className="input" value={pattern} onChange={(e) => setPattern(e.target.value)} data-testid="pattern-select">
                          <option value="squares">Classic Squares</option>
                          <option value="dots">Modern Dots</option>
                          <option value="rounded">Rounded Corners</option>
                        </select>
                      </div>
                      <div className="col-md-6">
                        <label className="label">Corner Style</label>
                        <select className="input" value={cornerStyle} onChange={(e) => setCornerStyle(e.target.value)} data-testid="corner-style-select">
                          <option value="square">Square Corners</option>
                          <option value="rounded">Rounded Corners</option>
                          <option value="dot">Dot Corners</option>
                        </select>
                      </div>
                    </div>

                    {/* Colors */}
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="label">Foreground Color</label>
                        <div className="d-flex gap-2">
                          <input type="color" value={fgColor} onChange={(e) => setFgColor(e.target.value)} style={{ width: '60px', height: '38px', cursor: 'pointer', borderRadius: 'var(--border-radius)' }} data-testid="fg-color-input" />
                          <input type="text" className="input" value={fgColor} onChange={(e) => setFgColor(e.target.value)} placeholder="#000000" style={{ fontFamily: 'monospace', flex: 1 }} />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <label className="label">Background Color</label>
                        <div className="d-flex gap-2">
                          <input type="color" value={bgColor} onChange={(e) => setBgColor(e.target.value)} style={{ width: '60px', height: '38px', cursor: 'pointer', borderRadius: 'var(--border-radius)' }} data-testid="bg-color-input" />
                          <input type="text" className="input" value={bgColor} onChange={(e) => setBgColor(e.target.value)} placeholder="#FFFFFF" style={{ fontFamily: 'monospace', flex: 1 }} />
                        </div>
                      </div>
                    </div>

                    {/* Error Correction */}
                    <div className="mb-3">
                      <label className="label">Error Correction Level</label>
                      <select className="input" value={errorCorrection} onChange={(e) => setErrorCorrection(e.target.value)} data-testid="error-correction-select">
                        <option value="L">Low (7%) - Simple QR codes</option>
                        <option value="M">Medium (15%) - Standard use</option>
                        <option value="Q">Quartile (25%) - Good with logos</option>
                        <option value="H">High (30%) - Best for logos</option>
                      </select>
                    </div>

                    {/* Logo Upload */}
                    <div className="mb-3">
                      <label className="label">Logo (Optional)</label>
                      <input type="file" accept="image/*" onChange={handleLogoUpload} className="input" data-testid="logo-upload-input" />
                      {logoFile && (
                        <div className="mt-2">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <p className="text-success mb-0" style={{ fontSize: '0.875rem' }}>✓ {logoFile.name}</p>
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() => {
                                setLogoFile(null);
                                setLogoFileId(null);
                                toast.success('Logo removed');
                              }}
                              data-testid="remove-logo-button"
                            >
                              🗑️ Remove
                            </button>
                          </div>
                          <label className="label">Logo Size: {logoSize}%</label>
                          <input type="range" min="10" max="30" value={logoSize} onChange={(e) => setLogoSize(parseInt(e.target.value))} style={{ width: '100%' }} data-testid="logo-size-slider" />
                          <p className="text-muted" style={{ fontSize: '0.75rem' }}>Recommended: 15-25% for best scannability</p>
                        </div>
                      )}
                    </div>

                    {/* QR Size */}
                    <div className="mb-4">
                      <label className="label">QR Code Size (for download): {size}px</label>
                      <input type="range" min="200" max="2000" step="50" value={size} onChange={(e) => setSize(parseInt(e.target.value))} style={{ width: '100%' }} data-testid="qr-size-slider" />
                      <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.75rem' }}>
                        <span>Small (200px)</span>
                        <span>Large (2000px)</span>
                      </div>
                    </div>

                    <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '1rem', marginTop: '1rem' }}></div>

                    {/* Artistic Features */}
                    <h5 className="mb-3">🎨 Artistic Branding</h5>

                    <div className="mb-3">
                      <label className="label">Frame Style</label>
                      <select className="input" value={frameStyle} onChange={(e) => setFrameStyle(e.target.value)} data-testid="frame-style-select">
                        <option value="none">No Frame</option>
                        <option value="rounded">Rounded Border</option>
                        <option value="circle">Circular Frame</option>
                        <option value="bottom">Bottom Text Banner</option>
                      </select>
                    </div>

                    {frameStyle !== 'none' && (
                      <div className="mb-3">
                        <label className="label">Frame Color</label>
                        <div className="d-flex gap-2">
                          <input type="color" value={frameColor} onChange={(e) => setFrameColor(e.target.value)} style={{ width: '60px', height: '38px', cursor: 'pointer', borderRadius: 'var(--border-radius)' }} />
                          <input type="text" className="input" value={frameColor} onChange={(e) => setFrameColor(e.target.value)} placeholder="#626ed4" style={{ fontFamily: 'monospace', flex: 1 }} />
                        </div>
                      </div>
                    )}

                    {frameStyle === 'bottom' && (
                      <div className="mb-3">
                        <label className="label">Frame Text</label>
                        <input
                          type="text"
                          className="input"
                          value={frameText}
                          onChange={(e) => setFrameText(e.target.value)}
                          placeholder="SCAN ME"
                          maxLength={20}
                          data-testid="frame-text-input"
                        />
                      </div>
                    )}

                    <div className="d-flex gap-2">
                      <button className="btn btn-light" onClick={() => setStep(1)} style={{ flex: 1 }}>← Back</button>
                      <button className="btn btn-primary" onClick={handleNext} style={{ flex: 1 }} data-testid="next-to-review">Next: Review →</button>
                    </div>
                  </div>
                )}

                {/* Step 3: Review */}
                {step === 3 && (
                  <div>
                    <h4 className="mb-4">Review & Save</h4>

                    <div className="mb-3">
                      <label className="label">Name</label>
                      <p style={{ fontWeight: 500 }}>{name}</p>
                    </div>

                    <div className="mb-3">
                      <label className="label">Destination URL</label>
                      <p style={{ fontWeight: 500, wordBreak: 'break-all' }}>{destinationUrl}</p>
                    </div>

                    <div className="mb-3">
                      <label className="label">Short URL</label>
                      <p style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>{qrValue}</p>
                    </div>

                    {(utmSource || utmMedium || utmCampaign) && (
                      <div className="alert alert-info mb-3">
                        <strong>UTM Parameters:</strong><br/>
                        {utmSource && <span className="badge badge-primary mr-2">Source: {utmSource}</span>}
                        {utmMedium && <span className="badge badge-primary mr-2">Medium: {utmMedium}</span>}
                        {utmCampaign && <span className="badge badge-primary">Campaign: {utmCampaign}</span>}
                      </div>
                    )}

                    <div className="mb-3">
                      <label className="label">Customization</label>
                      <div className="d-flex gap-2 mb-2" style={{ flexWrap: 'wrap' }}>
                        <span className="badge badge-primary">Pattern: {pattern}</span>
                        <span className="badge badge-info">Corner: {cornerStyle}</span>
                        <span className="badge badge-success">Size: {size}px</span>
                        <span className="badge badge-warning">ECC: {errorCorrection}</span>
                        {frameStyle !== 'none' && <span className="badge" style={{ background: 'rgba(111, 66, 193, 0.15)', color: '#6f42c1' }}>Frame: {frameStyle}</span>}
                        {logoFileId && <span className="badge" style={{ background: 'rgba(2, 164, 153, 0.15)', color: '#02a499' }}>Logo: {logoSize}%</span>}
                      </div>
                    </div>

                    {frameStyle !== 'none' && (
                      <div className="alert alert-success mb-3">
                        🎨 <strong>Artistic Frame Applied!</strong><br/>
                        Frame: {frameStyle} {frameStyle === 'bottom' && `| Text: "${frameText}"`}
                      </div>
                    )}

                    <div className="d-flex gap-2 mt-4">
                      <button className="btn btn-light" onClick={() => setStep(2)} style={{ flex: 1 }}>← Back</button>
                      <button className="btn btn-success" onClick={handleSave} disabled={loading} style={{ flex: 1 }} data-testid="save-qr-button">
                        {loading ? 'Saving...' : '✓ Save QR Code'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Live Preview Sidebar */}
            <div className="col-lg-4">
              <div className="card" style={{ position: 'sticky', top: '90px' }}>
                <h5 className="mb-3">Live Preview</h5>
                <div style={{ background: '#f8f9fa', padding: '2rem', borderRadius: 'var(--border-radius)', textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
                  <StyledQRCode
                    value={qrValue}
                    size={220}
                    fgColor={fgColor}
                    bgColor={bgColor}
                    pattern={pattern}
                    cornerStyle={cornerStyle}
                    logoUrl={logoFileId ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${logoFileId}` : null}
                    logoSize={logoSize / 100}
                    frameStyle={frameStyle}
                    frameText={frameText}
                    frameColor={frameColor}
                  />
                </div>
                <div style={{ marginTop: '1rem', padding: '1rem', background: 'var(--gray-100)', borderRadius: 'var(--border-radius)' }}>
                  <p className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>Short URL</p>
                  <p className="mb-0" style={{ fontSize: '0.875rem', fontFamily: 'monospace', wordBreak: 'break-all' }}>{qrValue}</p>
                </div>
                {frameStyle !== 'none' && (
                  <div className="alert alert-info mt-3" style={{ fontSize: '0.75rem', padding: '0.75rem' }}>
                    ✨ Artistic frame applied! Your QR will stand out.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateQR;
